import React, { useMemo, useState, useEffect } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import { FieldCustom } from 'components/molecules';
import { Editor } from '@tinymce/tinymce-react';
import styled from 'styled-components/macro';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { useEditorContext } from 'context/EditorContext';
import { defaultHtmlTable } from '../../services/constants';

const TableSchema = Yup.object().shape({
  title: Yup.string(),
  data: Yup.string()
    // .matches(/^\s*(<table[^>]*?>.*?<\/table>)\s*$/ig, 'este módulo apenas aceita uma tabela')
    .required('Campo obrigatório'),
});

const TablesEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps);
  const [{ screen }] = useEditorContext();

  const initialValues = useMemo(() => {
    const { title, data } = initialProps;
    return { title, data };
  }, [initialProps]);

  useEffect(() => {
    if (editorProps.data.length === 0) {
      const data = defaultHtmlTable;
      setEditorProps({ ...editorProps, data });
    }
  }, [editorProps]);

  useDebounce(
    () => {
      onChange(editorProps);
    },
    200,
    [editorProps]
  );

  const handleEditorChange = newData => {
    const regex = /(<table[^>]*border=")\d+("[^>]*>)/img;
    const data = newData.replace(regex, '$10$2');
    setEditorProps({ ...editorProps, data });
  };

  const handleFormChange = newValues => {
    const isValid = TableSchema.isValidSync(newValues);
    const hasSectionTitle = newValues.title.length !== 0;
    if (isValid) {
      setEditorProps({ ...initialProps, ...newValues, hasSectionTitle });
    }
  };

  return (
    <Section>
      <div className="container">
        <div className="row no-gutters">
          <div
            className={
              screen === 'desktop'
                ? 'col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-12 offset-0'
                : 'col-12'
            }
          >
            <Formik
              initialValues={initialValues}
              validationSchema={TableSchema}
              validate={handleFormChange}
            >
              <Form>
                <Field
                  title="Título"
                  name="title"
                  component={FieldCustom}
                  info="Insira um título para a tabela (opcional)"
                />
                <Editor
                  value={editorProps.data}
                  apiKey="isbmwc9p5iwyxi54b0883rnqm0w85mwvapeoyvhgb8tkmbnb"
                  init={{
                    height: 300,
                    width: '100%',
                    language: 'pt_PT',
                    menubar: false,
                    plugins: ['paste code link table lists'],
                    toolbar:
                      'undo redo | bold strikethrough | removeformat | table link',
                    toolbar_sticky: true,
                    content_css:
                      '//static.ua.pt/js/tinymce/5.4.0/editor.min.css',
                    forced_root_block: 'div',
                    valid_elements:
                      '-div,'
                      + 'a[href|target=_blank],'
                      + '-strong/-b,'
                      + 'br,'
                      + 'table[border=0],'
                      + 'table[style="width:100%"],'
                      + '-tr[rowspan|align|valign],'
                      + 'tbody,'
                      + 'thead,'
                      + 'tfoot,'
                      + '#td[colspan|rowspan|align|valign],'
                      + '#th[colspan|rowspan|align|valign]',
                    valid_children: '+body[table],-body[div] ',
                    toolbar_mode: 'sliding',
                    contextmenu: 'link image table',
                    document_base_url: 'http://www-new.ua.pt/',
                  }}
                  onEditorChange={handleEditorChange}
                />
              </Form>
            </Formik>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Section = styled.div`
  min-height: auto;
  padding-top: 4.375rem;
  padding-bottom: 4.375rem;
  background-color: #fff;
  position: relative;
`;

TablesEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    title: PropTypes.string,
    data: PropTypes.string,
  }),
};

TablesEditor.defaultProps = {
  initialProps: {},
};

export default TablesEditor;
